import {
  del,
  get,
  axiosConfig,
  type RequestConfig,
} from "../shared/api-client";
import type {
  ApiItemResponse,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";
import type {
  HospitalProfile,
  HospitalProfilePayload,
} from "@project/types/hospital-profile";
import {
  JIVANDHARA_BASE,
  SILENT_LIST,
  fetchListPage,
  unwrapItem,
} from "./list-api-helpers";

const BASE = `${JIVANDHARA_BASE}/hospital-profile`;
const SILENT: RequestConfig = { showNotification: false };

function appendIfPresent(fd: FormData, key: string, value: unknown) {
  if (value === undefined || value === null) {
    return;
  }
  if (value instanceof File) {
    fd.append(key, value);
    return;
  }
  if (Array.isArray(value) || typeof value === "object") {
    fd.append(key, JSON.stringify(value));
    return;
  }
  fd.append(key, String(value));
}

function toFormData(payload: Partial<HospitalProfilePayload>): FormData {
  const fd = new FormData();
  appendIfPresent(fd, "hospital_name", payload.hospital_name);
  appendIfPresent(fd, "subtitle", payload.subtitle);
  appendIfPresent(fd, "monthly_surgeries", payload.monthly_surgeries);
  appendIfPresent(fd, "hospital_timings", payload.hospital_timings);
  appendIfPresent(fd, "closed_on", payload.closed_on);
  appendIfPresent(fd, "new_patient_fee", payload.new_patient_fee);
  appendIfPresent(fd, "existing_patient_fee", payload.existing_patient_fee);
  appendIfPresent(fd, "emergency_contact_name", payload.emergency_contact_name);
  appendIfPresent(fd, "emergency_contact_number", payload.emergency_contact_number);
  appendIfPresent(fd, "highlights", payload.highlights);
  appendIfPresent(fd, "facilities", payload.facilities);
  appendIfPresent(fd, "treatments", payload.treatments);
  appendIfPresent(fd, "equipments", payload.equipments);
  if (payload.doctors && Array.isArray(payload.doctors)) {
    payload.doctors.forEach(docId => {
      fd.append("doctors", String(docId));
    });
  }
  appendIfPresent(fd, "nurses", payload.nurses);
  appendIfPresent(fd, "nibhav_yojana", payload.nibhav_yojana);
  appendIfPresent(fd, "is_active", payload.is_active);
  appendIfPresent(fd, "order_by", payload.order_by);
  appendIfPresent(fd, "description", payload.description);
  appendIfPresent(fd, "poem", payload.poem);

  if (payload.image !== undefined) {
    if (payload.image instanceof File) {
      fd.append("image", payload.image);
    } else if (typeof payload.image === "string") {
      fd.append("image", payload.image);
    } else if (payload.image === null) {
      fd.append("image", "");
    }
  }

  return fd;
}

class HospitalProfileService {
  listPage = (
    query: ListQuery = {}
  ): Promise<PaginatedResult<HospitalProfile>> => {
    return fetchListPage<HospitalProfile>(`${BASE}/`, query);
  };

  get = async (id: number): Promise<HospitalProfile | undefined> => {
    const data = await get<ApiItemResponse<HospitalProfile> | HospitalProfile>(
      `${BASE}/${id}/`,
      SILENT_LIST
    );
    return unwrapItem(data);
  };

  create = async (
    payload: HospitalProfilePayload
  ): Promise<HospitalProfile | undefined> => {
    const response = await axiosConfig.post<ApiItemResponse<HospitalProfile> | HospitalProfile>(
      `${BASE}/`,
      toFormData(payload),
      {
        ...SILENT,
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return unwrapItem(response.data);
  };

  update = async (
    id: number,
    payload: Partial<HospitalProfilePayload>
  ): Promise<HospitalProfile | undefined> => {
    const response = await axiosConfig.patch<ApiItemResponse<HospitalProfile> | HospitalProfile>(
      `${BASE}/${id}/`,
      toFormData(payload),
      {
        ...SILENT,
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return unwrapItem(response.data);
  };

  delete = async (id: number): Promise<void> => {
    await del(`${BASE}/${id}/`, SILENT);
  };
}

export const hospitalProfileService = new HospitalProfileService();
